/**
 * Slide-up preview drawer for blog templates (DSU: "template planner" —
 * clicking a template shows what the generated article will LOOK like).
 *
 * Renders a wireframe/skeleton mockup of the article shape implied by the
 * template's default_config: title, intro, sections with headings, image
 * placeholders, list block, FAQ (expanded, H3 questions — matching the
 * publish contract), and link hints. Pure client-side read of the config —
 * no backend call, mirroring AutoTemplateConfigPreview's approach.
 *
 * A1 honesty parity (W4): every shape-affecting read is sourced through
 * defaultConfigToRecipe (the sole deserializer) + clampRecipeToFloor(
 * parseShapeFloor(template.shape_floor)) — the words badge, section
 * range/label, FAQ count, and image presence display max(config, floor).
 * A floored axis carries the superscript floor tick (CONSULTS §4: the tick
 * is the sole in-line marking, NEVER a weight shift). Absent/malformed
 * payload = unclamped pass-through (clampToFloor's under-promise contract).
 * Detail-only reads with no floor axis (listType, imagePosition, link
 * counts, tone) stay on the raw config.
 *
 * v2 palette (W5 glyph treaty — ZERO teal on this surface):
 *   coral       = prose the writer generates (skeleton bars),
 *   ink outline = structured components (paper bg + ink-3 border),
 *   ink solid   = fixed chrome (H1 title bar, CTA).
 * H1/H2/H3 text tags are kept as redundant form cues — color is never the
 * sole signifier.
 */
import React, { useMemo } from 'react';
import {
  FileText,
  Image as ImageIcon,
  Link2,
  List,
  MessageCircleQuestion,
  Rocket,
  Sparkles,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { BlogTemplateSummary } from '@/services/blogTemplatesService';
import { defaultConfigToRecipe } from './recipePayload';
import { parseShapeFloor } from './shape/shapeFloor';
import { clampRecipeToFloor } from './shape/clampToFloor';
import { GALLERY_STRINGS } from './galleryStrings';

interface TemplatePreviewDrawerProps {
  template: BlogTemplateSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional — shows a "Use This Template" action in the footer. */
  onUse?: (template: BlogTemplateSummary) => void;
}

/**
 * Superscript floor tick (CONSULTS §4) — the SAME convention as the card's
 * spec line (TemplateCard): a raised tick whose tooltip is the footnote.
 * Sole in-line marking; color may second, never carry.
 */
const FloorTick: React.FC<{ note: string }> = ({ note }) => (
  <sup data-floor-tick="true" title={note} className="text-coral-dark">
    *
  </sup>
);

/** Coral skeleton bar standing in for a line of prose (v2: prose = coral). */
const Line: React.FC<{ w?: string; h?: string; className?: string }> = ({
  w = 'w-full',
  h = 'h-3',
  className = '',
}) => <div className={cn(h, w, 'rounded bg-coral', className)} />;

/** Heading-weight skeleton bar + neutral text tag (redundant form cue). */
const HeadingLine: React.FC<{ level: 2 | 3; label: string }> = ({ level, label }) => (
  <div className="flex items-center gap-2">
    <span className="text-[11px] font-bold text-ink-2 bg-paper border border-ink-3 rounded px-1 py-0.5">
      H{level}
    </span>
    <div
      className={cn(
        'rounded bg-coral',
        level === 2 ? 'h-4 w-2/5' : 'h-3 w-1/3',
      )}
      aria-label={label}
    />
  </div>
);

/** Structured component — v2 ink outline (paper bg + ink-3 border). */
const ImagePlaceholder: React.FC = () => (
  <div className="flex items-center justify-center h-24 rounded-md border border-ink-3 bg-paper text-ink-3">
    <ImageIcon className="h-5 w-5 mr-2" />
    <span className="text-xs">AI image (16:9)</span>
  </div>
);

export const TemplatePreviewDrawer: React.FC<TemplatePreviewDrawerProps> = ({
  template,
  open,
  onOpenChange,
  onUse,
}) => {
  // W4 — clamped data path: sole deserializer + served floor payload. No
  // clamp math here; every floored flag comes from clampRecipeToFloor.
  const recipe = useMemo(
    () => defaultConfigToRecipe(template?.default_config ?? {}),
    [template?.default_config],
  );
  const floor = useMemo(
    () => parseShapeFloor(template?.shape_floor),
    [template?.shape_floor],
  );
  const clamped = useMemo(() => clampRecipeToFloor(recipe, floor), [recipe, floor]);

  // Detail-only reads (no floor axis) — stay on the raw config (W4).
  const config = template?.default_config ?? {};
  const auto = config.autoConfig ?? {};

  const minSections = clamped.minSections.value;
  const maxSections = clamped.maxSections.value;
  // Render the midpoint so the mock feels representative, not maximal.
  const mockSections = Math.max(
    1,
    Math.min(6, Math.round((minSections + maxSections) / 2)),
  );
  const sectionsLabel =
    minSections === maxSections ? `${minSections}` : `${minSections}–${maxSections} (AI decides)`;

  const showImages = clamped.glyphs.image;
  const imagePerSection = auto.imagePosition === 'per-section';
  const imageAfterIntro =
    auto.imagePosition === 'after-intro' || auto.imagePosition === 'auto' || !auto.imagePosition;
  const showList = auto.includeList === true;
  const listOrdered = auto.listType === 'numbered' || auto.listType === 'lettered';
  const listItems = Math.min(auto.maxListItems ?? 5, 7);
  const showFaq = (config.includesFAQ ?? auto.includeFAQ) === true;
  // Clamped FAQ count feeding the same compact render cap (mock, not maximal).
  const faqItems = Math.min(clamped.faqItems.value, 5);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Centered drawer. The bottom Sheet variant is edge-to-edge (`inset-x-0`),
          so cap the width and let `mx-auto` center it between those insets —
          per Carl: keep it a slide-up drawer, just give it left/right spacing.
          Rounded top + side borders read correctly now that it floats inward.
          (Jaime, #1053 — folded into this PR per Carl ruling; stranded on
          staging as merge d9e50e90b, never reached main.) */}
      <SheetContent
        side="bottom"
        className="h-[82vh] p-0 flex flex-col mx-auto w-[calc(100%-4rem)] max-w-6xl rounded-t-2xl border-x"
      >
        <SheetHeader className="flex-shrink-0 px-6 pt-4 pb-3 border-b text-left">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="truncate">
                {template?.name ?? 'Template preview'}
              </SheetTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Layout preview — the article shape this template asks the AI to produce.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {config.targetWordCount && (
                <Badge variant="secondary" className="tabular-nums">
                  <FileText className="h-3 w-3 mr-1" />~{clamped.words.value.toLocaleString()} words
                  {clamped.words.floored && (
                    <FloorTick
                      note={`${GALLERY_STRINGS.specLine.floorTickNote} (≥ ${(floor?.rules.min_words ?? 0).toLocaleString('en-US')} words)`}
                    />
                  )}
                </Badge>
              )}
              {config.tone && <Badge variant="secondary">{config.tone}</Badge>}
              {config.includesSchema && (
                <Badge variant="secondary">
                  Schema
                </Badge>
              )}
              {onUse && template && (
                <Button
                  size="sm"
                  onClick={() => onUse(template)}
                >
                  <Rocket className="h-4 w-4 mr-1.5" />
                  Use This Template
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="max-w-2xl mx-auto my-6 px-8 py-8 bg-background border rounded-lg shadow-sm space-y-6">
            {/* Title + meta — H1 title bar is fixed chrome (v2: ink solid) */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-ink-2 bg-paper border border-ink-3 rounded px-1 py-0.5">
                  H1
                </span>
                <div className="h-5 w-3/4 rounded bg-ink" />
              </div>
              <Line w="w-1/4" h="h-2.5" className="bg-coral-light" />
            </div>

            {/* Intro */}
            <div className="space-y-2">
              <Line className="bg-coral-light" />
              <Line className="bg-coral-light" />
              <Line w="w-5/6" className="bg-coral-light" />
            </div>

            {imageAfterIntro && showImages && <ImagePlaceholder />}

            {/* Sections */}
            {Array.from({ length: mockSections }, (_, i) => (
              <div key={i} className="space-y-2.5 pt-2">
                <HeadingLine level={2} label={`Section ${i + 1} heading`} />
                <Line />
                <Line w="w-11/12" />
                <Line w="w-4/5" />
                {i === 0 && showList && (
                  <div className="pl-4 space-y-1.5 pt-1">
                    {Array.from({ length: listItems }, (_, li) => (
                      <div key={li} className="flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground w-4 text-right tabular-nums">
                          {listOrdered ? `${li + 1}.` : '•'}
                        </span>
                        <Line w={li % 2 ? 'w-2/3' : 'w-3/4'} h="h-2.5" />
                      </div>
                    ))}
                  </div>
                )}
                {i === 1 && <HeadingLine level={3} label="Subsection heading" />}
                {i === 1 && <Line w="w-3/4" />}
                {imagePerSection && showImages && <ImagePlaceholder />}
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground italic tabular-nums">
              <Sparkles className="h-3 w-3 inline mr-1" />
              {sectionsLabel} sections total
              {clamped.minSections.floored && (
                <FloorTick
                  note={`${GALLERY_STRINGS.specLine.floorTickNote} (≥ ${floor?.body_section_floor ?? 0} sections)`}
                />
              )}
            </p>

            {auto.imagePosition === 'end-only' && showImages && <ImagePlaceholder />}

            {/* FAQ — rendered EXPANDED with H3 questions, matching publish contract */}
            {showFaq && (
              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-center gap-2 pt-3">
                  <MessageCircleQuestion className="h-4 w-4 text-ink-3" />
                  <span className="text-sm font-semibold">
                    Frequently Asked Questions
                    {clamped.faqItems.floored && (
                      <FloorTick
                        note={`${GALLERY_STRINGS.specLine.floorTickNote} (≥ ${floor?.rules.min_faq_items ?? 0} FAQ items)`}
                      />
                    )}
                  </span>
                </div>
                {Array.from({ length: faqItems }, (_, qi) => (
                  <div key={qi} className="space-y-1.5">
                    <HeadingLine level={3} label={`FAQ question ${qi + 1}`} />
                    <Line w="w-11/12" h="h-2.5" />
                    <Line w="w-2/3" h="h-2.5" />
                  </div>
                ))}
              </div>
            )}

            {/* Link hints */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Link2 className="h-3.5 w-3.5 text-muted-foreground mt-2" />
              <div className="flex gap-1.5 flex-wrap pt-2">
                <Badge variant="outline" className="text-[11px] tabular-nums">
                  {config.internalLinkCount ?? auto.internalLinkCount ?? 'AI'} internal links
                </Badge>
                <Badge variant="outline" className="text-[11px] tabular-nums">
                  {config.externalLinkCount ?? auto.externalLinkCount ?? 'AI'} external links
                </Badge>
                {showList && (
                  <Badge variant="outline" className="text-[11px]">
                    <List className="h-3 w-3 mr-1" />
                    {auto.listType ?? 'auto'} list
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
